import {
    Button,
    Modal,
    Table,
    Row,
    Col,
    Alert,
    Form,
    Input,
    InputNumber,
    Select,
    DatePicker,
    message,
    ConfigProvider,
    theme
} from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';

import { useAuth } from "../../contexts/auth-context";
import {
    getSpecialPayments,
    saveSpecialPayment,
    archiveSpecialPayment
} from '../../rest/admin/specialPaymentsFromTo';

import './styles/AdminSpecialPaymentsFromTo.css';

const AdminSpecialPaymentsFromTo = (props) => {
    const { transactions = [], inProgress = false } = props;
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [newSpecialPaymentModal, setNewSpecialPaymentModal] = useState(false);
    const [newSpecialPaymentLoading, setNewSpecialPaymentLoading] = useState(false);
    const [error, setError] = useState(null);
    const [newSpecialPaymentError, setNewSpecialPaymentError] = useState(null);
    const auth = useAuth();
    const loginEmail = auth.currentUser?.email;
    const [newSpecialPaymentForm] = Form.useForm();

    const cryptoAccount = useSelector(state => state.cryptoAccount.account);
    const signedInWithMetamask = cryptoAccount.data?.exchange?.toLowerCase() === 'metamask';

    const fetchSpecialPayments = async () => {
        try {
            const response = await getSpecialPayments();
            if (response.data) {
                setData(response.data);
            }
            setLoadingData(false);
        } catch (error) {
            // If the error response indicates an expired token, sign out the user
            if (error.response && error.response.data?.errorCode === 'TOKEN_EXPIRED') {
                auth.signOut();
            }

            setLoadingData(false);
            const errorData = error.response?.data;
            if (errorData?.errorCode) { // This means the error is our customized error from the server
                setError(errorData.message);
                return;
            }

            setError(error.message);
        }
    }


    const onArchiveSpecialPayment = async (specialPayment) => {
        setLoading(true);

        try {
            await archiveSpecialPayment(specialPayment._id, { status: true });
            const response = await getSpecialPayments();
            if (response.data) {
                setData(response.data);
            }
            setLoading(false);

        } catch (error) {
            // If the error response indicates an expired token, sign out the user
            if (error.response && error.response.data?.errorCode === 'TOKEN_EXPIRED') {
                auth.signOut();
            }

            setLoading(false);
            const errorData = error.response?.data;
            if (errorData?.errorCode) { // This means the error is our customized error from the server
                setError(errorData.message);
                return;
            }

            if (errorData && typeof errorData === 'string') { // This means the error is our customized error from the server
                setError(errorData);
                return;
            }

            setError(error.message);
        }
    }

    const showArchiveModal = (specialPayment) => {
        Modal.confirm({
            title: 'Are you sure you want to archive this Special Payment?',
            content: (
                <p>
                    You won't be able to reverse this operation. Click yes if you're sure.
                </p>
            ),
            maskClosable: true,
            closable: true,
            visible: false, // Start hidden
            okText: 'Yes',
            onOk: () => {
                onArchiveSpecialPayment(specialPayment);
            },
            onCancel: () => setLoading(false)

        });
    }

    const columns = [
        {
            title: "Sender",
            dataIndex: "fromEmail",
            key: "fromEmail",
            fixed: "left",
            width: "200px",
        },
        {
            title: "Sender USDC",
            dataIndex: "fromCryptoAddress",
            key: "fromCryptoAddress",
            fixed: "left",
            width: "200px",
        },
        {
            title: "Recipient",
            dataIndex: "toEmail",
            key: "toEmail",
            // fixed: "left",
            width: "200px",
        },
        {
            title: "Recipient USDC",
            dataIndex: "toCryptoAddress",
            key: "toCryptoAddress",
            // fixed: "left",
            width: "200px",
        },
        // Temporarily archived: No use at the moment, we currently don't have partial special payments
        // GK 2026-06-03
        // {
        //     title: "Fee Amount Percenatge (%)",
        //     dataIndex: "fee_amount_percentage",
        //     key: "fee_amount_percentage",
        //     width: "150px",
        // },
        {
            title: "Archived",
            dataIndex: "archived",
            key: "archived",
            // fixed: "left",
            width: "200px",
            render: (value) => value ? 'yes' : 'no'
        },
        {
            title: "Action",
            dataIndex: "",
            key: "x",
            width: "200px",
            render: (_, record) => {
                return (
                    <Button
                        type="primary"
                        style={{ paddingLeft: '30px', paddingRight: '30px' }}
                        disabled={record.archived || loading}
                        onClick={() => showArchiveModal(record)}
                    >
                        Archive
                    </Button>
                )
            },
        },
    ];

    const handleNewSpecialPayment = async () => {
        setNewSpecialPaymentError('');
        setNewSpecialPaymentLoading(true);

        try {
            const formValues = await newSpecialPaymentForm.validateFields();
            await saveSpecialPayment(formValues);

            const response = await getSpecialPayments();
            if (response.data) {
                setData(response.data);
            }
            
            setNewSpecialPaymentModal(false);
            setNewSpecialPaymentLoading(false);
            newSpecialPaymentForm.resetFields();

        } catch (error) {
            // If the error response indicates an expired token, sign out the user
            if (error.response && error.response.data?.errorCode === 'TOKEN_EXPIRED') {
                auth.signOut();
            }

            setNewSpecialPaymentLoading(false);
            
            // If is AntDesign validation errors, just return as they are already displayed on the screen
            if (error.errorFields?.length) {
                return;
            }
            
            const errorData = error.response?.data;
            if (errorData?.errorCode) { // This means the error is our customized error from the server
                setNewSpecialPaymentError(errorData.message);
                return;
            }

            if (errorData && typeof errorData === 'string') { // This means the error is our customized error from the server
                setNewSpecialPaymentError(errorData);
                return;
            }

            setNewSpecialPaymentError(error.message);
        }
    }

    useEffect(() => {
        fetchSpecialPayments();
    }, [])


    const validateMessages = {
        required: "${label} is required!",
        types: {
            email: "${label} is not a valid email!",
            number: "${label} is not a valid number!",
        },
        number: {
            range: "${label} must be between ${min} and ${max}",
        },
    };

    return (
        <div className="admin-special-payments-page">
            <h2 className="text-center">Special Payments</h2>
            {
                error && (
                    <Row style={{ marginBottom: '20px' }}>
                        <Col
                            xs={{ span: 24 }}
                            md={{ span: 16, offset: 4 }}
                            style={{ marginTop: "10px" }}
                        >
                            <Alert className="text-center" title={error} type="error" />
                        </Col>
                    </Row>
                )
            }
            <div className="text-white">
                <Row justify="end" align="middle">
                    <Col className="admin-new-special-payments-col" span="24">
                        <Button
                            type="primary"
                            onClick={() => setNewSpecialPaymentModal(true)}
                        >
                            New Special Payment
                        </Button>
                    </Col>
                </Row>
            </div>
            <Table
                columns={columns}
                dataSource={data}
                scroll={{ x: 1024 }}
                loading={loadingData}
            //onRow={onRow}
            />
            <ConfigProvider
                theme={{
                    algorithm: theme.defaultAlgorithm,
                }}
            >
                <Modal // Info Modal for Coinbase Signin
                    title="New Special Payment:"
                    centered
                    open={newSpecialPaymentModal}
                    onOk={() => handleNewSpecialPayment()}
                    okText="Save special payment"
                    onCancel={() => setNewSpecialPaymentModal(false)}
                    confirmLoading={newSpecialPaymentLoading}
                // width={700} // width in px
                >

                    {
                        newSpecialPaymentError && (
                            <Row style={{ marginBottom: '20px' }}>
                                <Col
                                    xs={{ span: 24 }}
                                    md={{ span: 16, offset: 4 }}
                                    style={{ marginTop: "10px" }}
                                >
                                    <Alert title={newSpecialPaymentError} type="error" />
                                </Col>
                            </Row>
                        )
                    }

                    <Form
                        labelCol={{
                            // xs: { span: 24 },
                            // md: { span: 24 }
                        }}
                        wrapperCol={{
                            // xs: { span: 24 },
                            // md: { span: 24 },
                        }}
                        form={newSpecialPaymentForm}
                        name="newSpecialPayment"
                        // onFinish={onFinish}
                        validateMessages={validateMessages}
                        // style={{  margin: "auto", }}
                    >

                        <Form.Item
                            name={"fromEmail"}
                            label={"fromEmail"}
                            rules={[{ required: true }]}
                            className="form-item"
                        >
                            <Input placeholder={"Sender"} maxLength={225} disabled={newSpecialPaymentLoading} />
                        </Form.Item>
                        <Form.Item
                            name={"toEmail"}
                            label={"toEmail"}
                            rules={[{ required: true }]}
                            className="form-item"
                        >
                            <Input placeholder={"Recipient"} maxLength={225} disabled={newSpecialPaymentLoading} />
                        </Form.Item>
                    </Form>
                </Modal>
            </ConfigProvider>
        </div>
    );
};

export default AdminSpecialPaymentsFromTo;
